import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  Grid,
  Alert,
  Snackbar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  MenuItem,
  Paper,
  Chip,
  Tabs,
  Tab
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import { addDocument, getCollection, updateDocument, deleteDocument } from '../../services/firestoreService';

function TabPanel({ children, value, index }) {
  return (
    <div role="tabpanel" hidden={value !== index}>
      {value === index && <Box sx={{ py: 3 }}>{children}</Box>}
    </div>
  );
}

const ManageBreeds = () => {
  const [tabValue, setTabValue] = useState(0);
  const [breedName, setBreedName] = useState('');
  const [species, setSpecies] = useState('Canino');
  const [breeds, setBreeds] = useState([]);
  const [loading, setLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [editingBreed, setEditingBreed] = useState(null);

  useEffect(() => {
    fetchBreeds();
  }, []);

  const fetchBreeds = async () => {
    try {
      const breedsData = await getCollection('breeds');
      setBreeds(breedsData);
    } catch (error) {
      console.error('Error fetching breeds:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const breedData = {
        name: breedName.trim(),
        species: species,
        createdAt: new Date()
      };

      if (editingBreed) {
        await updateDocument('breeds', editingBreed.id, breedData);
        setSnackbar({
          open: true,
          message: 'Raza actualizada exitosamente',
          severity: 'success'
        });
        setEditingBreed(null);
      } else {
        await addDocument('breeds', breedData);
        setSnackbar({
          open: true,
          message: 'Raza agregada exitosamente',
          severity: 'success'
        });
      }

      // Limpiar formulario
      setBreedName('');
      setSpecies('Canino');

      // Actualizar lista
      fetchBreeds();
    } catch (error) {
      console.error('Error saving breed:', error);
      setSnackbar({
        open: true,
        message: 'Error al guardar la raza',
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (breed) => {
    setEditingBreed(breed);
    setBreedName(breed.name);
    setSpecies(breed.species);
  };

  const handleCancelEdit = () => {
    setEditingBreed(null);
    setBreedName('');
    setSpecies('Canino');
  };

  const handleDelete = async (breedId) => {
    if (window.confirm('¿Estás seguro de eliminar esta raza?')) {
      try {
        await deleteDocument('breeds', breedId);
        setSnackbar({
          open: true,
          message: 'Raza eliminada exitosamente',
          severity: 'success'
        });
        fetchBreeds();
      } catch (error) {
        console.error('Error deleting breed:', error);
        setSnackbar({
          open: true,
          message: 'Error al eliminar la raza',
          severity: 'error'
        });
      }
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const filteredBreeds = breeds.filter(breed => {
    if (tabValue === 0) return breed.species === 'Canino';
    if (tabValue === 1) return breed.species === 'Felino';
    return true;
  });

  const handleSeedDogBreeds = async () => {
    if (!window.confirm('¿Deseas cargar las razas de perros predefinidas? Esto agregará múltiples razas a la base de datos.')) {
      return;
    }

    setLoading(true);
    const dogBreeds = [
      'Mestizo', 'Labrador Retriever', 'Golden Retriever', 'Pastor Alemán', 'Poodle (Caniche)',
      'Bulldog Francés', 'Bulldog Inglés', 'Pug', 'Salchicha (Teckel)', 'Yorkshire Terrier',
      'Schnauzer', 'Cocker Spaniel', 'Bichón Frisé', 'Chihuahua', 'Shih Tzu', 'Beagle',
      'Border Collie', 'Siberian Husky', 'Boxer', 'Rottweiler', 'Doberman', 'Gran Danés',
      'Dalmatian', 'Pitbull Terrier Americano', 'Bull Terrier', 'Fila Brasileño', 'Akita Inu',
      'Shiba Inu', 'Samoyedo', 'Corgi', 'Jack Russell Terrier', 'West Highland White Terrier',
      'Fox Terrier', 'Setter Irlandés', 'Braco Alemán', 'Pointer Inglés',
      'Vizsla', 'Basset Hound', 'Galgo', 'San Bernardo'
    ];

    try {
      for (const breed of dogBreeds) {
        await addDocument('breeds', {
          name: breed,
          species: 'Canino',
          createdAt: new Date()
        });
      }
      setSnackbar({
        open: true,
        message: `${dogBreeds.length} razas de perros agregadas exitosamente`,
        severity: 'success'
      });
      fetchBreeds();
    } catch (error) {
      console.error('Error seeding breeds:', error);
      setSnackbar({
        open: true,
        message: 'Error al cargar razas predefinidas',
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSeedCatBreeds = async () => {
    if (!window.confirm('¿Deseas cargar las razas de gatos predefinidas?')) {
      return;
    }

    setLoading(true);
    const catBreeds = [
      'Mestizo', 'Persa', 'Siamés', 'Maine Coon', 'Angora',
      'Bengalí', 'Ragdoll', 'British Shorthair', 'Scottish Fold', 'Sphynx',
      'Abisinio', 'Birmano', 'Exotic Shorthair', 'Devon Rex', 'Bombay'
    ];

    try {
      for (const breed of catBreeds) {
        await addDocument('breeds', {
          name: breed,
          species: 'Felino',
          createdAt: new Date()
        });
      }
      setSnackbar({
        open: true,
        message: `${catBreeds.length} razas de gatos agregadas exitosamente`,
        severity: 'success'
      });
      fetchBreeds();
    } catch (error) {
      console.error('Error seeding breeds:', error);
      setSnackbar({
        open: true,
        message: 'Error al cargar razas predefinidas',
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box>
      <Typography variant="h5" gutterBottom>
        Gestión de Razas
      </Typography>
      <Typography variant="body2" color="text.secondary" gutterBottom>
        Administra las razas de perros y gatos disponibles en el sistema
      </Typography>
      <Paper sx={{ p: 3, mt: 3, bgcolor: 'background.default' }}>
        <Box component="form" onSubmit={handleSubmit}>
          <Grid container spacing={2}>
            <Grid
              size={{
                xs: 12,
                sm: 6
              }}>
              <TextField
                fullWidth
                label="Nombre de la Raza"
                value={breedName}
                onChange={(e) => setBreedName(e.target.value)}
                required
                placeholder="Ej: Labrador Retriever"
              />
            </Grid>
            <Grid
              size={{
                xs: 12,
                sm: 6
              }}>
              <TextField
                fullWidth
                select
                label="Especie"
                value={species}
                onChange={(e) => setSpecies(e.target.value)}
                required
              >
                <MenuItem value="Canino">Canino</MenuItem>
                <MenuItem value="Felino">Felino</MenuItem>
              </TextField>
            </Grid>
            <Grid size={12}>
              <Button
                type="submit"
                variant="contained"
                disabled={loading}
                sx={{ mr: 1 }}
              >
                {loading ? 'Guardando...' : editingBreed ? 'Actualizar Raza' : 'Agregar Raza'}
              </Button>
              {editingBreed && (
                <Button
                  variant="outlined"
                  onClick={handleCancelEdit}
                >
                  Cancelar
                </Button>
              )}
            </Grid>
          </Grid>
        </Box>
      </Paper>
      {/* Botones de carga rápida */}
      <Box sx={{ mt: 2, display: 'flex', gap: 2 }}>
        <Button
          variant="outlined"
          onClick={handleSeedDogBreeds}
          disabled={loading}
          size="small"
        >
          Cargar Razas de Perros Predefinidas
        </Button>
        <Button
          variant="outlined"
          onClick={handleSeedCatBreeds}
          disabled={loading}
          size="small"
        >
          Cargar Razas de Gatos Predefinidas
        </Button>
      </Box>
      {/* Lista de razas con tabs */}
      <Box sx={{ mt: 4 }}>
        <Typography variant="h6" gutterBottom>
          Razas Registradas
        </Typography>
        <Paper>
          <Tabs
            value={tabValue}
            onChange={(e, newValue) => setTabValue(newValue)}
            variant="fullWidth"
          >
            <Tab label={`Caninos (${breeds.filter(b => b.species === 'Canino').length})`} />
            <Tab label={`Felinos (${breeds.filter(b => b.species === 'Felino').length})`} />
            <Tab label={`Todas (${breeds.length})`} />
          </Tabs>

          <TabPanel value={tabValue} index={tabValue}>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Nombre</TableCell>
                    <TableCell>Especie</TableCell>
                    <TableCell align="right">Acciones</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredBreeds.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={3} align="center">
                        No hay razas registradas
                      </TableCell>
                    </TableRow>
                  ) : (
                    filteredBreeds.map((breed) => (
                      <TableRow key={breed.id}>
                        <TableCell>{breed.name}</TableCell>
                        <TableCell>
                          <Chip
                            label={breed.species}
                            color={breed.species === 'Canino' ? 'primary' : 'secondary'}
                            size="small"
                          />
                        </TableCell>
                        <TableCell align="right">
                          <IconButton
                            size="small"
                            onClick={() => handleEdit(breed)}
                            color="primary"
                          >
                            <EditIcon />
                          </IconButton>
                          <IconButton
                            size="small"
                            onClick={() => handleDelete(breed.id)}
                            color="error"
                          >
                            <DeleteIcon />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </TabPanel>
        </Paper>
      </Box>
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ManageBreeds;
